import { DatePipe } from '@angular/common'
import { DateFormat, TimeFormat } from '../../enums/application/date-format.enum'

const DD_MM_YYYY_SLASH = /^\d{1,2}\/\d{1,2}\/\d{4}$/
const YYYY_MM_DD_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/
const UTC_ISO_PATTERN = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})?$/

// Date -> 2023-06-31
export function formatDateForSQL(period: Date | string | null | undefined): string {
  if (!period) return ''

  if (typeof period === 'string') {
    const trimmed = period.trim()
    if (trimmed === '') return ''

    // datas em string representam dia civil, converter via Date aplicaria o fuso e deslocaria o dia
    const isoMatch = trimmed.match(YYYY_MM_DD_PREFIX)
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`

    if (DD_MM_YYYY_SLASH.test(trimmed)) {
      const [day, month, year] = trimmed.split('/')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
  }

  const date: Date = new Date(period)

  if (isNaN(date.getTime())) return ''

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  let dateFormat: string = `${year}-${month}-${day}`

  return dateFormat
}

export function formatDateForISO8601(period: Date | number): string {
  const date: Date = new Date(period)

  let dateFormat: string = `${date.getFullYear()}-${date.getMonth() + 1 > 9 ? date.getMonth() + 1 : '0' + (date.getMonth() + 1)}-${date.getDate() > 9 ? date.getDate() : '0' + date.getDate()}`

  return dateFormat
}

// dd/mm/yyyy -> Date
export function formatDateStringForDateCalendar(period: string | null): Date {
  if (period !== null) {
    let dateFragmentString: Array<string> = period.split('/'),
      dateFragmentNumber: Array<number> = new Array(),
      dateCalendar: Date = new Date()

    dateFragmentNumber = dateFragmentString.map((fragmentDate: string) => {
      return Number(fragmentDate)
    })

    dateCalendar = new Date(dateFragmentNumber[2], dateFragmentNumber[1] - 1, dateFragmentNumber[0], 3)

    return dateCalendar
  } else return new Date()
}

export function formatDateForISO8601WithHours(period: Date | number, useTimeZoneOffSet: boolean = true): string {
  const dateFormat: string = formatDateForISO8601(period)
  const date: Date = new Date(period)

  // 2023-02-01 12:00:00-03:00

  return `${dateFormat} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}${useTimeZoneOffSet ? `-${date.getTimezoneOffset()}:${date.getMilliseconds()}` : ''}`
}

/**
 * Hoje às 19hr30 - Ontem às 19hr30 - 10/11/2022 às 19hr 30
 **/
export function formatDateForWhatsappFormat(period: Date, isIncludeTimezone: boolean = false, ignoreRelativeLabels: boolean = false, separateTimeCaracter: 'hr' | ':' = 'hr'): string {
  const date = new Date(period)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  if (isIncludeTimezone) date.setHours(date.getHours() + 3)

  const formatDate = (d: Date): string => `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
  const formatTime = (d: Date): string => `${d.getHours()}${separateTimeCaracter}${d.getMinutes().toString().padStart(2, '0')}`

  const fullDate = formatDate(date)

  if (ignoreRelativeLabels) return `${fullDate} às ${formatTime(date)}`

  const fullToday = formatDate(today)
  const fullYesterday = formatDate(yesterday)

  if (fullDate === fullToday) return `Hoje às ${formatTime(date)}`
  else if (fullDate === fullYesterday) return `Ontem às ${formatTime(date)}`
  else return `${fullDate} às ${formatTime(date)}`
}

export function formatDateForDatePipeString(period: Date | string | number | null | undefined, format: string = 'dd/MM/yyyy', timezone: string = '+003'): string {
  const datePipe: DatePipe = new DatePipe('pt-BR')

  if (period === null || period === undefined) return ''

  let date: Date

  if (period instanceof Date) {
    date = period
  } else if (typeof period === 'number') {
    date = new Date(period)
  } else {
    const trimmed = period.trim()
    if (trimmed === '') return ''

    if (DD_MM_YYYY_SLASH.test(trimmed)) {
      date = formatDateStringForDateCalendar(trimmed)
    } else {
      date = new Date(trimmed)
    }
  }

  if (Number.isNaN(date.getTime())) return ''

  const dateFormat = datePipe.transform(date, format, timezone) as string

  return dateFormat ?? ''
}

// 2023-06-31 -> Date
export function formatDateSQLforDateCalendar(dateSQLFormat: string | null): Date {
  if (dateSQLFormat !== null) {
    let arrayDate: Array<number> = []

    arrayDate = dateSQLFormat.split('-').map((item) => Number(item))

    return new Date(arrayDate[0], arrayDate[1] - 1, arrayDate[2])
  }
  return new Date()
}

export function formatTimestampforDateCalendar(timestamp: number | null | Date, isIncludeTimezone: boolean = false): Date {
  if (timestamp !== null) {
    let date: Date = new Date(timestamp)

    if (isIncludeTimezone) date.setHours(date.getHours() + 3)

    return date
  }
  return new Date()
}

// Função que converte de 'yyyy-mm-dd' (ou ISO) para 'dd/mm/yyyy' ou 'dd/mm/yyyy hh:mm'
export function formatDateSQLForDatePipeString(dateSQL: string | null | undefined, hasTime: boolean = false): string {
  if (!dateSQL) return ''
  if (dateSQL.includes('/')) return dateSQL

  const datePart = dateSQL.substring(0, 10)
  const datePattern = /^\d{4}-\d{2}-\d{2}$/

  if (!datePattern.test(datePart)) throw new Error('Formato de data inválido. Use o formato YYYY-MM-DD.')

  const [year, month, day] = datePart.split('-')
  const formattedDate = `${day}/${month}/${year}`

  if (!hasTime) return formattedDate

  const timePart = dateSQL.substring(11, 16)
  const timePattern = /^\d{2}:\d{2}$/

  return timePattern.test(timePart) ? `${formattedDate} ${timePart}` : formattedDate
}

// Função que converte de '2026-09-03T14:04:58.417000Z' para o formato escolhido, no fuso do navegador
export function formatDateUTCForLocalString(utcDate: string | null | undefined, format: DateFormat = DateFormat.SHORT, hasTime: boolean = false, timeFormat: TimeFormat = TimeFormat.HOUR_MINUTE): string {
  if (!utcDate) return ''

  const match = utcDate.trim().match(UTC_ISO_PATTERN)
  if (!match) return ''

  const [, dateTime, fraction, offset] = match
  const milliseconds = (fraction ?? '').substring(0, 3).padEnd(3, '0')
  const date: Date = new Date(`${dateTime}.${milliseconds}${offset ?? 'Z'}`)

  if (Number.isNaN(date.getTime())) return ''

  const datePipe: DatePipe = new DatePipe('pt-BR')

  return datePipe.transform(date, hasTime ? `${format} ${timeFormat}` : format) ?? ''
}

// Função que converte de 'dd/mm/yyyy' para 'yyyy-mm-dd'
export function formatDatePipeStringForDateSQL(dateStr: string): string {
  const [day, month, year] = dateStr.split('/')
  return `${year}-${month}-${day}`
}

export function formatDateForISO8601WithTime(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0')
  const timezoneOffset = -date.getTimezoneOffset()
  const offsetHours = String(Math.floor(Math.abs(timezoneOffset) / 60)).padStart(2, '0')
  const offsetMinutes = String(Math.abs(timezoneOffset) % 60).padStart(2, '0')
  const offsetSign = timezoneOffset >= 0 ? '+' : '-'

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetSign}${offsetHours}:${offsetMinutes}`
}

// "Thu, 01 Jan 2026 00:00:00 GMT" -> Date (local timezone)
export function formatDateGMTForDateCalendar(dateGMT: string | Date): Date {
  const dateString = String(dateGMT)
  const dateParts = dateString.split(' ')
  const day = Number(dateParts[1])
  const monthMap: { [key: string]: number } = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
  const month = monthMap[dateParts[2]]
  const year = Number(dateParts[3])

  return new Date(year, month, day, 12, 0, 0)
}
